import { lookupSalesProduct } from "../../services/studioSalesApi";
import { fetchPickerProducts, PickerProduct, ProductPageLoader } from "../../components/products/productSelection";

/** Only approved business reasons are shown; arbitrary framework messages never become UI copy. */
export function salesSelectionReason(value: unknown, fallback = "This product could not be added. Refresh the memo and try again."): string {
  const message = String(value || "");
  if (message.startsWith("On another memo")) return "This product is selected on another billing memo. Remove it from that memo first.";
  if (message.startsWith("Already sold")) return "This product has already been sold.";
  const reasons: Record<string, string> = {
    "This piece is out of stock": "This product is out of stock.",
    "This studio is not the current product owner": "Your studio is not the current product owner.",
    "This product is not available": "This product is no longer available.",
    "Product not found": "This product could not be found. Refresh inventory and try again.",
    "This piece is already on this memo": "This product is already on this memo.",
    "Only a Draft memo can be edited. Cancel and recreate a Ready for Billing memo.": "This memo is no longer a draft. Return to the memo to check its status.",
  };
  return reasons[message] || fallback;
}

async function checkedProduct(product: PickerProduct): Promise<PickerProduct> {
  try {
    const response: any = await lookupSalesProduct(product.productUniqueId);
    const data = response?.data;
    if (!response?.success || data?.exact !== true || data.product?.productUniqueId !== product.productUniqueId) {
      return { ...product, canAdd: false, reason: "We couldn't check availability. Refresh inventory and try again." };
    }
    return { ...product, ...data.product, canAdd: data.canAdd === true && data.product.canAdd === true,
      reason: salesSelectionReason(data.reason || data.product.reason, "This product is not available for this memo.") };
  } catch {
    return { ...product, canAdd: false, reason: "We couldn't check availability. Check your connection and refresh inventory." };
  }
}

export const loadSalesProductPage: ProductPageLoader = async (query, page, limit) => {
  if (query.exact) {
    const response: any = await lookupSalesProduct(query.exact);
    if (!response?.success) throw new Error("Lookup unavailable");
    const data = response.data;
    if (!data?.exact || !data.product?.productUniqueId) return { products: [], total: 0 };
    const exact = data.product;
    // Exact scans intentionally override browse filters (including stock), but never eligibility.
    const details = await fetchPickerProducts({ ...query, exact: "", filters: { ProductId: exact.productUniqueId } }, 0, limit);
    return { products: [{ ...details.products.find((p) => p.productUniqueId === exact.productUniqueId), ...exact,
      canAdd: data.canAdd === true && exact.canAdd === true,
      reason: salesSelectionReason(data.reason || exact.reason, "This product is not available for this memo.") }], total: 1 };
  }
  const result = await fetchPickerProducts(query, page, limit);
  if (query.filters?.picker) return result;
  // There is no Sales bulk eligibility endpoint. Limit exact lookups to four at a time.
  const products: PickerProduct[] = [];
  for (let index = 0; index < result.products.length; index += 4) {
    products.push(...await Promise.all(result.products.slice(index, index + 4).map(checkedProduct)));
  }
  return { ...result, products };
};
